import { useMutation } from '@tanstack/react-query'

import { BIOMETRIA_INVALIDA } from '@/features/biometria/types/biometria'
import { isBiometriaCancelled, obterAssercao } from '@/features/biometria/services/webauthn'
import { codigoDoErro, normalizeApiError } from '@/lib/apiEnvelope'
import { refreshSession } from '@/lib/httpClient'
import { queryClient } from '@/lib/queryClient'
import { authStore } from '@/stores/authStore'

import { authApi } from '../api/authApi'

export type ResultadoDesbloqueio =
  /** O `status` da sessão já foi resolvido (desbloqueada, login ou refresh normal). */
  | { tipo: 'resolvido' }
  /** Leitor fechado: fica na tela, sem mensagem. */
  | { tipo: 'cancelado' }
  /** Biometria recusada, aparelho desativado, 429 ou rede: mostra a mensagem. */
  | { tipo: 'erro'; mensagem: string; biometriaRecusada: boolean }

/**
 * Desbloqueio da sessão travada (401 `BIOMETRIA_NECESSARIA` no refresh). Cada tentativa pede
 * um desafio novo. 401 = sessão morta (vai para o login); 409 = a sessão não usa biometria
 * (refresh normal). Os dois resolvem o `status` sozinhos.
 */
export function useDesbloqueio() {
  return useMutation({
    mutationFn: async (): Promise<ResultadoDesbloqueio> => {
      try {
        const { desafioId, opcoes } = await authApi.opcoesDesbloqueio()
        const resposta = await obterAssercao(opcoes)
        authStore.getState().setSession(await authApi.desbloquear({ desafioId, resposta }))
        return { tipo: 'resolvido' }
      } catch (error) {
        if (isBiometriaCancelled(error)) return { tipo: 'cancelado' }

        const normalizado = normalizeApiError(error)
        if (normalizado.status === 401) {
          authStore.getState().clearSession()
          return { tipo: 'resolvido' }
        }
        if (normalizado.status === 409) {
          // `refreshSession` já marca authenticated, locked ou unauthenticated.
          await refreshSession().catch(() => {})
          return { tipo: 'resolvido' }
        }
        return {
          tipo: 'erro',
          mensagem: normalizado.detail,
          biometriaRecusada: codigoDoErro(error) === BIOMETRIA_INVALIDA,
        }
      }
    },
  })
}

/** "Entrar com senha": encerra a sessão no servidor (cookie) e volta para o login. */
export function useSairDoDesbloqueio() {
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      authStore.getState().clearSession()
      queryClient.clear()
    },
  })
}
